import { formatDate } from "app/main/utils/dateUtils";
import Icon from "@material-ui/core/Icon";
import Tooltip from "@material-ui/core/Tooltip";
import Box from "@material-ui/core/Box";
import LinearProgress from "@material-ui/core/LinearProgress";
import moment from "moment";
import { convertSearchRequestToFilters, saveSearchesSessionStorage } from "app/main/searchV3/utils/searchUtils";

const getStateColor = (state) => {
	switch (state) {
		case "FINISHED":
		case "FINISHED_PRELOADED":
			return "text-green-700";
		case "FINISHED_WITH_FAILURES":
		case "PARTIALLY_FINISHED":
			return "text-orange-700";
		case "PROCESSING":
		case "QUEUEING":
			return "text-blue-700";
		case "EXPIRED":
			return "text-grey-500";
		case "FAILED":
		case "FAILED_STUCK":
			return "text-red-700";
		default:
			return "text-grey-700";
	}
};

const getExpirationReasonLabel = (reason) => {
	switch (reason) {
		case "PREV_HAS_FAILURES":
			return "Failures";
		case "USER_OFFLINE":
			return "Offline";
		case "USER_CLOSE":
			return "Closed";
		case "FORCE_EXPIRED":
			return "Force Expired";
		case "LOAD_ACCEPTED":
			return "Booked";
		default:
			return reason;
	}
};

// Expiration status -> icon/color/label, styled like the internalTripsList status icons.
const getExpirationStatusConfig = (item) => {
	const isExpired = !!item.expirationReason || moment(item.expiresAt).isBefore(moment());
	if (item.expirationReason === "LOAD_ACCEPTED") {
		return { icon: "check_circle", color: "text-blue", label: "Booked" };
	}
	if (isExpired) {
		return { icon: "timer_off", color: "text-grey-500", label: "Expired" };
	}
	return { icon: "autorenew", color: "text-green", label: "Searching" };
};

// Helper function to launch a search with the same filters and preserve search ID
const handleSearchWithSameFilters = async (searchRequest, history, dispatch) => {
	try {
		const searchFilters = convertSearchRequestToFilters(searchRequest);
		await saveSearchesSessionStorage(searchFilters);
		history.push(`/search?id=${searchRequest._id}`);
	} catch (error) {
		console.error("Error launching search with same filters:", error);
	}
};

const botSearchesList = (dispatch) => ({
	urlGET: "api/profile/carriers/:carrierId/bot-searches",
	idKey: "_id",
	listenEvent: "searchRevision",
	segmentKey: "BOT_SEARCHES",
	content: {
		grid: {
			tilesPerRow: 2,
			itemsPerRow: 2,
		},
		table: {
			variant: "skinny",
			orderBy: ["createdAt", "updatedAt", "expiresAt", "origin", "destination", "availStart"],
		},
		pagination: {
			enabled: true,
			resetScroll: true,
		},
		order: {
			defaut: {
				key: "updatedAt",
				dir: "desc",
			},
		},
		defaults: {
			initial: {
				filters: {
					createdAt: "__DATE_DAYS_GT__7",
					expirationStatus: "__NOT_SELECTED__",
				},
				groups: {},
			},
			items: [
				{
					label: "All",
					filters: {
						expirationStatus: "__NOT_SELECTED__",
						createdAt: "__DATE_DAYS_GT__7",
					},
				},
				{
					label: "Active",
					filters: {
						expirationStatus: "ACTIVE",
						createdAt: "__DATE_DAYS_GT__7",
					},
				},
				{
					label: "Expired",
					filters: {
						expirationStatus: "EXPIRED",
						createdAt: "__DATE_DAYS_GT__7",
					},
				},
			],
		},
		filters: {
			search: true,
			items: [
				{
					key: "expirationStatus",
					type: "select",
					label: "Status",
					defaultValue: "__NOT_SELECTED__",
					options: [
						{
							value: "__NOT_SELECTED__",
							label: "All",
						},
						{
							value: "ACTIVE",
							label: "Active",
						},
						{
							value: "EXPIRED",
							label: "Expired",
						},
					],
				},
				{
					key: "truck",
					type: "autocomplete",
					label: "Truck",
					translate: "",
					field: {
						noErrorMessage: true,
						classes: {},
						variant: undefined,
					},
					autocomplete: {
						provider: "smarthop",
						url: "api/profile/trucks",
						params: {
							options_active_only: true,
							options_format_show_driver_phone: true,
						},
						preloadDefault: true,
						preloadImmedately: true,
						listenEvent: "profileRevision",
					},
					rules: {
						params: {
							dataRef: "carrierId",
							paramKey: "options_carrier_id_search",
						},
					},
				},
				{
					key: "trigger",
					type: "select",
					label: "Trigger",
					defaultValue: "__NOT_SELECTED__",
					options: [
						{
							value: "__NOT_SELECTED__",
							label: "All",
						},
						{
							value: "USER",
							label: "User",
						},
						{
							value: "MARKET_MONITOR",
							label: "Market Monitor",
						},
						{
							value: "MARKET_SCAN",
							label: "Market Scan",
						},
					],
				},
				{
					key: "createdAt",
					type: "select",
					label: "Created At",
					defaultValue: "__DATE_DAYS_GT__7",
					options: [
						{
							value: "__DATE_DAYS_GT__1",
							label: "Last 24 Hours",
						},
						{
							value: "__DATE_DAYS_GT__3",
							label: "Last 3 Days",
						},
						{
							value: "__DATE_DAYS_GT__7",
							label: "Last 7 Days",
						},
						{
							value: "__DATE_DAYS_GT__30",
							label: "Last 30 Days",
						},
					],
				},
			],
		},
		groups: {
			items: [
				{
					key: "internal",
					type: "checkbox",
					label: "Show Internal Data",
				},
			],
		},
		items: [
			{
				key: "_id",
				type: "id",
				label: "Search ID",
				group: "internal",
			},
			{
				key: "trigger",
				type: "text",
				label: "Trigger",
				group: "internal",
			},
			{
				key: "updatedAt",
				type: "component",
				label: "Status",
				builder: (item) => {
					const config = getExpirationStatusConfig(item);
					const tooltip = (
						<div className="flex flex-col p-4">
							<span className="font-semibold">{config.label}</span>
							{item.expirationReason ? <span>Reason: {getExpirationReasonLabel(item.expirationReason)}</span> : null}
							<span>Expires: {item.expiresAt ? formatDate(item.expiresAt) : "-"}</span>
							<span>Ready: {item.availStart ? formatDate(item.availStart) : "-"}</span>
							<span>Created: {item.createdAt ? formatDate(item.createdAt) : "-"}</span>
							<span>Updated: {item.updatedAt ? formatDate(item.updatedAt) : "-"}</span>
						</div>
					);
					return (
						<div className="flex flex-row items-center">
							<Tooltip title={tooltip} arrow>
								<Icon className={config.color + " text-22 mr-6"}>{config.icon}</Icon>
							</Tooltip>
							<span className="text-11 text-grey-600">{item.createdAt ? formatDate(item.createdAt) : "-"}</span>
						</div>
					);
				},
			},
			{
				key: "origin",
				type: "component",
				label: "Lane",
				builder: (item) => {
					const destination = item?.destination || item?.destinationStates?.join(", ");
					return (
						<span className="text-12">
							{item?.origin || "-"}
							{destination ? ` → ${destination}` : ""}
						</span>
					);
				},
			},
			{
				key: "availStart",
				type: "component",
				label: "Ready",
				table: { width: 90 },
				builder: (item) => (
					<span className="text-11 text-grey-600">{item.availStart ? formatDate(item.availStart) : "-"}</span>
				),
			},
			{
				label: "Dynamic Status",
				type: "component",
				group: "internal",
				builder: (item) => {
					const state = item?.dynamicStatus?.state;
					return <span className={`font-medium ${getStateColor(state)}`}>{state || "UNKNOWN"}</span>;
				},
			},
			{
				key: "agent",
				type: "component",
				label: "Agent Stats",
				table: { width: 200 },
				builder: (item) => {
					const total = item?.analytics?.counters?.total ?? 0;
					const best = item?.analytics?.counters?.best ?? 0;
					const hopbest = item?.analytics?.counters?.hopbest ?? 0;
					const newTotal = item?.analytics?.counters?.newTotal ?? 0;
					const contactedCalled = item?.analytics?.counters?.contacted ?? 0;
					const emailSent = item?.analytics?.counters?.emailSent ?? 0;
					const contactedEnd = item?.analytics?.counters?.contactedEnd ?? 0;
					const emailReplied = item?.analytics?.counters?.emailReplied ?? 0;
					const covered = item?.analytics?.counters?.covered ?? 0;
					const escalated = item?.escalatedCount ?? 0;
					const contacted = contactedCalled + emailSent;

					const metrics = [
						{
							icon: "playlist_add_check",
							color: "text-grey-600",
							value: `${newTotal} / ${total}`,
							tooltip: `Results: ${newTotal} new of ${total} total`,
						},
						{
							icon: "star",
							color: "text-amber-700",
							value: `${best} / ${hopbest}`,
							tooltip: `Best matches: ${best} / Forced: ${hopbest}`,
						},
						{
							icon: "check_circle",
							color: "text-green",
							value: `${covered} / ${contacted}`,
							tooltip: `Covered: ${covered} of ${contacted} contacted`,
						},
						{
							icon: "phone",
							color: "text-blue",
							value: `${contactedEnd} / ${contactedCalled}`,
							tooltip: `Calls answered: ${contactedEnd} of ${contactedCalled} called`,
						},
						{
							icon: "email",
							color: "text-purple",
							value: `${emailReplied} / ${emailSent}`,
							tooltip: `Email replies: ${emailReplied} of ${emailSent} sent`,
						},
						{
							icon: "priority_high",
							color: "text-orange",
							value: `${escalated}`,
							tooltip: `Escalated loads: ${escalated}`,
						},
					];

					return (
						<div className="grid grid-cols-2 gap-x-12 gap-y-2">
							{metrics.map((metric) => (
								<Tooltip key={metric.icon} title={metric.tooltip} arrow>
									<div className="flex flex-row items-center">
										<Icon className={`${metric.color} text-16 mr-6`}>{metric.icon}</Icon>
										<span className="text-12 text-grey-700">{metric.value}</span>
									</div>
								</Tooltip>
							))}
						</div>
					);
				},
			},
			{
				key: "latestActedLoad",
				type: "component",
				label: "Latest Load",
				builder: (item) => {
					const load = item?.latestActedLoad;
					if (!load) {
						return <span className="text-11 text-grey-500">-</span>;
					}
					return (
						<div className="flex flex-col min-w-256 max-w-384">
							{(load.origin || load.destination) && (
								<span className="text-11 text-grey-600">
									{load.origin || "-"}
									{load.destination ? ` → ${load.destination}` : ""}
								</span>
							)}
							{load.reason ? (
								<span className="text-11 text-grey-700 whitespace-normal break-words">{load.reason}</span>
							) : null}
							{load.price != null && (
								<span className="text-12 font-medium">${Number(load.price).toLocaleString()}</span>
							)}
						</div>
					);
				},
			},
			{
				key: "outcome",
				type: "component",
				label: "Market",
				builder: (item) => {
					const rateMedian = item?.analytics?.stats?.rate?.median;
					const rateTop = item?.analytics?.stats?.rate?.q90;
					const rpmMedian = item?.analytics?.stats?.rpm?.median;
					const rpmTop = item?.analytics?.stats?.rpm?.q90;
					const hasRange = rateMedian != null && rateTop != null && rateTop > 0;
					const pct = hasRange ? Math.min(100, Math.max(0, (rateMedian / rateTop) * 100)) : 0;
					if (!hasRange) {
						return <span className="text-11 text-grey-500">-</span>;
					}
					return (
						<div className="flex flex-col min-w-160">
							<Box className="flex flex-row justify-between mb-2">
								<span className="text-10 text-grey-700 font-medium">${Math.round(rateMedian)}</span>
								<span className="text-10 text-grey-700 font-medium">${Math.round(rateTop)}</span>
							</Box>
							<LinearProgress variant="determinate" value={pct} style={{ height: 6, borderRadius: 4 }} />
							<Box className="flex flex-row justify-between mt-2">
								<span className="text-10 text-grey-500">Median</span>
								<span className="text-10 text-grey-500">Q90</span>
							</Box>
							{rpmMedian != null ? (
								<span className="text-10 text-grey-500">
									${rpmMedian.toFixed(2)}
									{rpmTop != null ? ` – $${rpmTop.toFixed(2)}` : ""}/mi
								</span>
							) : null}
						</div>
					);
				},
			},
			{
				key: "truck__view",
				type: "truck__view",
				label: "Truck",
			},
			{
				key: "trip__view",
				type: "trip__view",
				label: "Booked Trip",
				viewConfig: {
					showStatus: true,
				},
			},
			{
				key: "menu",
				type: "menu",
				label: "Menu",
				items: [
					{
						key: "go_to_search",
						label: "Go to Search",
						icon: "search",
						dispatchAction: (item, dataIds, dispatch, history, snackbar) => {
							handleSearchWithSameFilters(item, history, dispatch);
						},
					},
					{
						key: "view_results",
						label: "View Full Results",
						newTab: true,
						redirectURLBuilder: (item) => `/admin/search/requests/${item._id}/details/user/${item.user}`,
					},
					{
						variant: "divider",
					},
					{
						key: "retrigger_search",
						label: "Re-Start Search",
						dialogBuilder: (item) => ({
							viewId: "CONFIRM_DIALOG",
							mode: "CREATE",
							dataIds: {
								title: "Re-Start Search",
								message: `This will resume the search using the same filters as search (${item._id}).\n\nOrigin: ${
									item.origin || "Any"
								}\nDestination: ${item.destination || item.destinationStates?.join(", ") || "Anywhere"}`,
								confirmLabel: "Re-Start",
								url: `api/search/requests/${item._id}/retrigger`,
								method: "POST",
								listenEvent: "searchRevision",
								body: {
									shouldResume: true,
									...(item.analytics?.options ? { options: item.analytics.options } : {}),
								},
							},
						}),
					},
					{
						variant: "divider",
					},
					{
						key: "force_expire",
						label: "Stop Search",
						dialogBuilder: (item) => ({
							viewId: "CONFIRM_DIALOG",
							mode: "DELETE",
							dataIds: {
								title: "Stop Search",
								message: `Are you sure you want to stop the agent for this search (${item._id})? This action cannot be undone.`,
								confirmLabel: "Stop Search",
								url: `api/search/requests/${item._id}/force-expire`,
								listenEvent: "searchRevision",
							},
						}),
					},
				],
			},
		],
	},
});

export default botSearchesList;
